import _isEqual from 'lodash/isEqual'
import _max from 'lodash/max'
import { useNextSanityImage } from 'next-sanity-image'
import Image from 'next/image'
import { memo, useEffect, useMemo, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'
import { z } from 'zod'

import Stack from '@nxt-insurance/ui-kit/components/layoutComponents/Stack'
import { SCREEN_SIZE, SPACE } from '@nxt-insurance/ui-kit/constants'
import { body1 } from '@nxt-insurance/ui-kit/core/typography'

import { sanityClientLike } from '@/cms/client'
import { horizontalAlignmentEnum } from '@/cms/helperTypes'
import { imageSchema } from '@/cms/image'
import { useWindowWidth } from '@/hooks/useWindowWidth'
import { itemsAlignmentTailwind } from '@/utils/style'
import type { PortableTextTypeComponentProps } from '@portabletext/react'

import { ConfigurableTypography, configurableTypography } from './ConfigurableTypography'

const imageListSwitcher = z.object({
  _key: z.string(),
  _type: z.literal('imageListSwitcher'),
  switchImageInterval: z.number().optional(),
  imageHorizontalAlignment: horizontalAlignmentEnum.optional(),
  inverse: z.boolean().optional(),
  maxTextWidth: z.number().optional(),
  elements: z.array(
    z.object({
      _key: z.string(),
      title: configurableTypography,
      subtitle: z.string().optional(),
      image: imageSchema.merge(
        z.object({
          alt: z.string(),
          imageWidth: z.number().optional(),
        })
      ),
    })
  ),
})

type ImageListSwitcherSchema = z.infer<typeof imageListSwitcher>

type ListElement = ImageListSwitcherSchema['elements'][number]

const imageListStyles = tv({
  slots: {
    container: 'flex w-full flex-col lg:flex-row',
    textContainer: 'relative flex flex-1 flex-col justify-center lg:self-start lg:pl-0 lg:pr-16',
  },
  variants: {
    inverse: {
      true: {
        container: 'lg:flex-row-reverse',
        textContainer: 'pr-0 lg:self-end lg:pl-16',
      },
    },
  },
})

const Text = (props: {
  title: ListElement['title']
  maxWidth?: number
  subtitle: ListElement['subtitle']
}) => (
  <Stack space="xsmall" style={{ maxWidth: props.maxWidth }}>
    <ConfigurableTypography
      node={{
        index: 0,
        isInline: false,
        renderNode: () => null,
        value: { _key: '1', ...props.title },
      }}
      align={props.title.textAlign ?? 'left'}
    />
    {props.subtitle ? <p className={body1({ color: 'uiDarkMedium' })}>{props.subtitle}</p> : null}
  </Stack>
)

const Elements = (props: {
  selectedItem: number
  pause: (index: number) => void
  play: () => void
  elements: ListElement[]
  width: number
  isMobile: boolean
  inverse: boolean
  maxTextWidth?: number
  imageHorizontalAlignment: z.infer<typeof horizontalAlignmentEnum>
}) => {
  const selectedImage = useMemo(
    () => props.elements[props.selectedItem].image,
    [props.elements, props.selectedItem]
  )

  const [imageContainerHeight, imageContainerWidth] = useMemo(() => {
    const widths = props.elements.map((element) => element.image.imageWidth ?? props.width)
    const heights = props.elements.map((element) => {
      const imageWidth = element.image.imageWidth ?? props.width
      if ('_ref' in element.image.asset) {
        const dimensions = element.image.asset._ref.split('-')[1]

        const [width, height] = dimensions.split('x').map(Number.parseInt)
        const aspectRatio = width / height
        return imageWidth / aspectRatio
      }
      return imageWidth / element.image.asset.metadata.dimensions.aspectRatio
    })
    return [_max(heights), _max(widths)]
  }, [props.elements, props.width])

  const imageProps = useNextSanityImage(sanityClientLike, selectedImage ?? null, {
    imageBuilder: (builder) => builder.width((selectedImage.imageWidth ?? props.width) * 2),
  })

  const { container, textContainer } = imageListStyles({ inverse: props.inverse })
  const windowWidth = useWindowWidth()

  return (
    <div className={container()}>
      <div className="relative flex flex-col">
        <div className={textContainer()}>
          {props.elements.map((element, index) => (
            <button
              className={twMerge(
                'my-6 flex flex-col border-l-2 border-l-transparent bg-transparent pl-4 outline-none transition-all duration-200 hover:border-l-brand-dark lg:my-8 lg:flex-row lg:items-center lg:pl-[72px]',
                index === props.selectedItem && 'border-l-brand-dark'
              )}
              key={element._key}
              onMouseEnter={() => (props.isMobile ? undefined : props.pause(index))}
              onClick={() => (props.isMobile ? props.pause(index) : undefined)}
              onMouseLeave={props.isMobile ? undefined : props.play}
            >
              <Text
                maxWidth={props.maxTextWidth}
                title={element.title}
                subtitle={element.subtitle}
              />
            </button>
          ))}
        </div>
      </div>
      <div
        className={twMerge(
          'flex flex-1 flex-col items-center pt-8 lg:justify-center lg:pt-0',
          `lg:${itemsAlignmentTailwind[props.imageHorizontalAlignment]}`
        )}
        style={{ height: imageContainerHeight }}
      >
        <Image
          {...imageProps}
          className="h-auto"
          width={imageProps.width / 2}
          height={imageProps.height / 2}
          alt={selectedImage.alt}
          style={{
            maxWidth:
              imageContainerWidth && windowWidth > imageContainerWidth
                ? imageContainerWidth
                : `calc(100vw - ${SPACE.gutter * 2}px)`,
          }}
        />
      </div>
    </div>
  )
}

enum Status {
  Paused,
  Playing,
}

const pauseTimeMobile = 7000

export const ImageListSwitcher = memo(
  (props: { node: PortableTextTypeComponentProps<unknown>; width: number }) => {
    const value = imageListSwitcher.parse(props.node.value)
    const [selectedItem, setSelectedItem] = useState(0)
    const [status, setStatus] = useState(Status.Playing)
    const windowWidth = useWindowWidth()

    useEffect(() => {
      if (typeof window === 'undefined') {
        return
      }
      const interval = setInterval(() => {
        if (status === Status.Paused) {
          if (windowWidth < SCREEN_SIZE.L) {
            setTimeout(() => {
              setStatus(Status.Playing)
            }, pauseTimeMobile)
          }
          return
        }
        setSelectedItem((prev) => (prev + 1) % value.elements.length)
      }, value.switchImageInterval ?? 3000)

      return () => clearInterval(interval)
    }, [value.elements.length, value.switchImageInterval, status, windowWidth])

    const pause = (index: number) => {
      setSelectedItem(index)
      setStatus(Status.Paused)
    }
    const play = () => {
      setStatus(Status.Playing)
    }

    return (
      <Elements
        selectedItem={selectedItem}
        pause={pause}
        play={play}
        imageHorizontalAlignment={value.imageHorizontalAlignment ?? 'center'}
        inverse={value.inverse ?? false}
        elements={value.elements}
        width={props.width}
        maxTextWidth={value.maxTextWidth}
        isMobile={windowWidth < SCREEN_SIZE.L}
      />
    )
  },
  _isEqual
)
